import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  ViewChild,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { collection, doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes, uploadBytesResumable } from 'firebase/storage';
import { db, storage, handleFirestoreError, OperationType } from './firebase';
import { AuthService } from './auth.service';
import { NotificationService } from './notification';
import { UserImage } from './models/image.model';

export type FramingState = 'needs_adjustment' | 'approaching_fit' | 'perfectly_framed';

@Component({
  selector: 'app-add-image',
  imports: [CommonModule, MatIconModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (isOpen()) {
      <div
        class="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-5 md:p-6 bg-black/70 dark:bg-black/85 backdrop-blur-xl animate-ios-fade"
        (click)="onBackdropClick($event)"
      >
        <div
          class="relative w-full max-w-sm sm:max-w-md md:max-w-lg rounded-[28px] sm:rounded-[36px] bg-white dark:bg-[#121217] border border-black/10 dark:border-white/10 shadow-[0_25px_70px_-15px_rgba(0,0,0,0.5)] overflow-hidden flex flex-col max-h-[94vh] animate-ios-slide"
          (click)="$event.stopPropagation()"
        >
          <!-- Ambient Background Glows -->
          <div
            class="absolute -top-24 -left-24 w-52 h-52 bg-ios-blue/15 rounded-full blur-[80px] pointer-events-none"
          ></div>
          <div
            class="absolute -bottom-24 -right-24 w-52 h-52 bg-purple-500/15 rounded-full blur-[80px] pointer-events-none"
          ></div>

          <!-- Modal Header -->
          <div
            class="relative z-10 px-5 sm:px-6 py-4 border-b border-black/5 dark:border-white/5 flex items-center justify-between"
          >
            <div class="flex items-center gap-3">
              <div
                class="w-9 h-9 sm:w-10 sm:h-10 rounded-2xl bg-ios-blue/10 dark:bg-ios-blue/20 text-ios-blue flex items-center justify-center border border-ios-blue/20 shadow-sm shrink-0"
              >
                <mat-icon class="!text-[20px] sm:!text-[22px]">crop_free</mat-icon>
              </div>
              <div>
                <h2
                  class="text-base sm:text-lg font-black tracking-tight text-slate-900 dark:text-white"
                >
                  Add Image
                </h2>
                <p class="text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:text-zinc-400">
                  1:1 Frame Alignment
                </p>
              </div>
            </div>

            <button
              type="button"
              (click)="close()"
              class="w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center text-slate-500 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white hover:bg-black/5 dark:hover:bg-white/10 active:scale-95 transition-all cursor-pointer"
              aria-label="Close modal"
            >
              <mat-icon class="!text-[20px]">close</mat-icon>
            </button>
          </div>

          <!-- Modal Body -->
          <div
            class="relative z-10 flex-1 overflow-y-auto scrollbar-none [&::-webkit-scrollbar]:hidden p-4 sm:p-6 space-y-4"
          >
            <!-- 1:1 Square Frame Display -->
            <div class="space-y-3">
              <div
                #dropFrame
                class="relative w-full aspect-square rounded-2xl sm:rounded-3xl overflow-hidden bg-zinc-950 flex items-center justify-center border-2 transition-all duration-300 touch-none group select-none"
                [class.border-red-500]="selectedImagePreview() && framingState() === 'needs_adjustment'"
                [class.shadow-[0_0_20px_rgba(239,68,68,0.35)]]="selectedImagePreview() && framingState() === 'needs_adjustment'"
                [class.border-amber-400]="selectedImagePreview() && framingState() === 'approaching_fit'"
                [class.shadow-[0_0_20px_rgba(251,191,36,0.35)]]="selectedImagePreview() && framingState() === 'approaching_fit'"
                [class.border-emerald-500]="selectedImagePreview() && framingState() === 'perfectly_framed'"
                [class.shadow-[0_0_20px_rgba(16,185,129,0.35)]]="selectedImagePreview() && framingState() === 'perfectly_framed'"
                [class.border-black/10]="!selectedImagePreview()"
                [class.dark:border-white/10]="!selectedImagePreview()"
                (dragover)="onDragOver($event)"
                (dragleave)="onDragLeave($event)"
                (drop)="onDrop($event)"
                (wheel)="onWheelZoom($event)"
              >
                @if (selectedImagePreview(); as preview) {
                  <!-- Draggable, Scalable & Rotatable Image -->
                  <div
                    class="relative w-full h-full overflow-hidden flex items-center justify-center cursor-grab active:cursor-grabbing"
                    (mousedown)="startPan($event)"
                    (mousemove)="onPan($event)"
                    (mouseup)="endPan()"
                    (mouseleave)="endPan()"
                    (touchstart)="startTouchPan($event)"
                    (touchmove)="onTouchPan($event)"
                    (touchend)="endTouchPan()"
                  >
                    <img
                      #previewImg
                      [src]="preview"
                      alt="Kicgle Flyer Image"
                      (load)="onImageLoaded($event)"
                      class="absolute max-w-none origin-center pointer-events-none transition-transform duration-75"
                      [style.width.px]="displayedBaseWidth()"
                      [style.height.px]="displayedBaseHeight()"
                      [style.transform]="transformStyle()"
                      referrerpolicy="no-referrer"
                    />
                  </div>

                  <!-- Small Text Framing Status Label -->
                  <div
                    class="absolute top-3 left-3 z-20 flex items-center gap-2 px-3 py-1.5 rounded-full backdrop-blur-md text-xs font-black shadow-md uppercase tracking-wider transition-all"
                    [class.bg-red-500/80]="framingState() === 'needs_adjustment'"
                    [class.text-white]="framingState() === 'needs_adjustment'"
                    [class.bg-amber-400/90]="framingState() === 'approaching_fit'"
                    [class.text-slate-950]="framingState() === 'approaching_fit'"
                    [class.bg-emerald-500/90]="framingState() === 'perfectly_framed'"
                    [class.text-white]="framingState() === 'perfectly_framed'"
                  >
                    <span class="w-2 h-2 rounded-full animate-pulse bg-current"></span>
                    <span>
                      @switch (framingState()) {
                        @case ('needs_adjustment') { Needs Adjustment }
                        @case ('approaching_fit') { Approaching Fit }
                        @case ('perfectly_framed') { Perfectly Framed }
                      }
                    </span>
                  </div>

                  <!-- Image Action Floating Icons (Replace & Remove) -->
                  <div class="absolute top-3 right-3 flex items-center gap-1.5 z-20">
                    <button
                      type="button"
                      (click)="fileInput.click()"
                      class="w-8 h-8 rounded-full bg-black/65 hover:bg-black/85 backdrop-blur-md text-white shadow-md flex items-center justify-center transition-all cursor-pointer"
                      title="Replace Image"
                      aria-label="Replace Image"
                    >
                      <mat-icon class="!text-[16px]">sync</mat-icon>
                    </button>
                    <button
                      type="button"
                      (click)="removeSelectedImage()"
                      class="w-8 h-8 rounded-full bg-red-500/80 hover:bg-red-600 backdrop-blur-md text-white shadow-md flex items-center justify-center transition-all cursor-pointer"
                      title="Remove Image"
                      aria-label="Remove Image"
                    >
                      <mat-icon class="!text-[16px]">close</mat-icon>
                    </button>
                  </div>
                } @else {
                  <!-- Empty Dropzone -->
                  <div
                    (click)="fileInput.click()"
                    (keydown.enter)="fileInput.click()"
                    tabindex="0"
                    class="flex flex-col items-center justify-center p-6 text-center cursor-pointer hover:bg-white/5 active:scale-98 transition-all w-full h-full"
                  >
                    <div
                      class="w-14 h-14 sm:w-16 sm:h-16 rounded-3xl bg-ios-blue/10 text-ios-blue flex items-center justify-center mb-3 shadow-sm border border-ios-blue/20"
                    >
                      <mat-icon class="!text-[28px] sm:!text-[32px]">photo_camera</mat-icon>
                    </div>
                    <p class="text-sm font-black text-white mb-1">
                      Upload Kicgle Flyer Image
                    </p>
                    <p class="text-xs text-zinc-400 mb-3 max-w-[220px]">
                      Drag & drop or tap to select image for 1:1 framing
                    </p>
                    <span
                      class="px-4 py-1.5 rounded-full bg-ios-blue text-white text-xs font-bold shadow-sm"
                    >
                      Browse Image
                    </span>
                  </div>
                }
              </div>

              <input
                #fileInput
                type="file"
                accept="image/*"
                class="hidden"
                (change)="onFileSelected($event)"
              />

              <!-- Manual Zoom Slider & Icon-Only Action Buttons (Rotate & Reset) -->
              @if (selectedImagePreview()) {
                <div
                  class="p-3 rounded-2xl bg-black/5 dark:bg-white/[0.03] border border-black/5 dark:border-white/10 flex items-center justify-between gap-3"
                >
                  <!-- Zoom Slider -->
                  <div class="flex-1 flex items-center gap-2">
                    <button
                      type="button"
                      (click)="stepZoom(-0.05)"
                      class="text-slate-500 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white transition-colors flex items-center cursor-pointer"
                      title="Zoom Out"
                      aria-label="Zoom Out"
                    >
                      <mat-icon class="!text-[18px]">zoom_out</mat-icon>
                    </button>

                    <input
                      type="range"
                      min="0.3"
                      max="3.0"
                      step="0.01"
                      [value]="zoomScale()"
                      (input)="onZoomChange($event)"
                      class="flex-1 accent-ios-blue h-1.5 bg-black/10 dark:bg-white/15 rounded-lg cursor-pointer"
                    />

                    <button
                      type="button"
                      (click)="stepZoom(0.05)"
                      class="text-slate-500 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white transition-colors flex items-center cursor-pointer"
                      title="Zoom In"
                      aria-label="Zoom In"
                    >
                      <mat-icon class="!text-[18px]">zoom_in</mat-icon>
                    </button>

                    <span
                      class="text-xs font-mono font-extrabold text-slate-700 dark:text-zinc-300 min-w-[36px] text-right"
                    >
                      {{ (zoomScale() * 100).toFixed(0) }}%
                    </span>
                  </div>

                  <!-- Icon-Only Buttons for Rotate and Reset -->
                  <div class="flex items-center gap-1.5 shrink-0 border-l border-black/10 dark:border-white/10 pl-3">
                    <button
                      type="button"
                      (click)="rotateRight()"
                      class="w-9 h-9 rounded-xl bg-black/5 dark:bg-white/10 hover:bg-black/10 dark:hover:bg-white/20 text-slate-800 dark:text-white flex items-center justify-center transition-all cursor-pointer active:scale-90"
                      title="Rotate 90°"
                      aria-label="Rotate image 90 degrees"
                    >
                      <mat-icon class="!text-[18px]">rotate_right</mat-icon>
                    </button>

                    <button
                      type="button"
                      (click)="resetTransform()"
                      class="w-9 h-9 rounded-xl bg-black/5 dark:bg-white/10 hover:bg-black/10 dark:hover:bg-white/20 text-slate-800 dark:text-white flex items-center justify-center transition-all cursor-pointer active:scale-90"
                      title="Reset Position & Zoom"
                      aria-label="Reset frame transform"
                    >
                      <mat-icon class="!text-[18px]">restart_alt</mat-icon>
                    </button>
                  </div>
                </div>
              }

              <!-- Framing Adjustment Instructions when not perfectly framed -->
              @if (selectedImagePreview() && framingState() !== 'perfectly_framed') {
                <div
                  class="p-3 rounded-2xl bg-amber-500/10 border border-amber-500/20 text-amber-600 dark:text-amber-400 text-xs font-bold flex items-center gap-2"
                >
                  <mat-icon class="!text-[18px] shrink-0">info</mat-icon>
                  <span>Adjust zoom and drag position until the frame turns green (Perfectly Framed) to enable upload.</span>
                </div>
              }
            </div>

            <!-- Visibility Settings Toggle -->
            <div
              class="p-3.5 rounded-2xl bg-black/5 dark:bg-white/[0.03] border border-black/5 dark:border-white/10 flex items-center justify-between gap-3"
            >
              <div class="flex items-center gap-2.5">
                <div
                  class="w-8 h-8 rounded-xl flex items-center justify-center transition-colors"
                  [class.bg-emerald-500/15]="isPublic()"
                  [class.text-emerald-500]="isPublic()"
                  [class.bg-slate-500/15]="!isPublic()"
                  [class.text-slate-400]="!isPublic()"
                >
                  <mat-icon class="!text-[18px]">
                    {{ isPublic() ? 'public' : 'lock' }}
                  </mat-icon>
                </div>
                <div>
                  <span class="text-xs font-black text-slate-900 dark:text-white block">
                    {{ isPublic() ? 'Public Setting' : 'Private Setting' }}
                  </span>
                  <span class="text-[10px] text-slate-500 dark:text-zinc-400 block">
                    {{ isPublic() ? 'Initial pending state before public search approval' : 'Only visible to you' }}
                  </span>
                </div>
              </div>

              <!-- Switch Toggle -->
              <button
                type="button"
                role="switch"
                [attr.aria-checked]="isPublic()"
                (click)="toggleVisibility()"
                class="w-11 h-6.5 rounded-full p-0.5 transition-colors duration-200 relative focus:outline-none shrink-0 cursor-pointer"
                [class.bg-emerald-500]="isPublic()"
                [class.bg-zinc-400]="!isPublic()"
                [class.dark:bg-zinc-700]="!isPublic()"
              >
                <div
                  class="w-5.5 h-5.5 rounded-full bg-white shadow-md transform transition-transform duration-200"
                  [class.translate-x-4.5]="isPublic()"
                  [class.translate-x-0]="!isPublic()"
                ></div>
              </button>
            </div>

            <!-- Error Feedback -->
            @if (errorMessage()) {
              <div
                class="p-3 rounded-2xl bg-red-500/10 border border-red-500/20 text-xs font-bold text-red-500 flex items-center gap-2"
              >
                <mat-icon class="!text-[16px]">error</mat-icon>
                <span>{{ errorMessage() }}</span>
              </div>
            }

            <!-- Footer Action Buttons -->
            <div class="pt-1 flex items-center gap-3">
              <button
                type="button"
                (click)="close()"
                class="flex-1 h-11 sm:h-12 rounded-2xl font-bold text-xs uppercase tracking-wider text-slate-600 dark:text-zinc-300 hover:bg-black/5 dark:hover:bg-white/5 active:scale-95 transition-all cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                (click)="submitImage()"
                [disabled]="isSubmitting() || !selectedFile() || framingState() !== 'perfectly_framed'"
                class="flex-[2] h-11 sm:h-12 rounded-2xl font-black text-xs uppercase tracking-wider bg-ios-blue text-white shadow-ios shadow-ios-blue/25 hover:bg-ios-blue/95 active:scale-98 transition-all disabled:opacity-40 disabled:pointer-events-none flex items-center justify-center gap-2 cursor-pointer"
              >
                @if (isSubmitting()) {
                  <div
                    class="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"
                  ></div>
                  <span>Uploading...</span>
                } @else {
                  <mat-icon class="!text-[18px]">cloud_upload</mat-icon>
                  <span>Upload Image</span>
                }
              </button>
            </div>
          </div>
        </div>
      </div>
    }

    <!-- Dedicated Progress Popup Modal -->
    @if (showProgressModal()) {
      <div class="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/75 dark:bg-black/85 backdrop-blur-xl animate-ios-fade">
        <div class="w-full max-w-sm rounded-[32px] bg-white dark:bg-zinc-900 border border-black/10 dark:border-white/10 p-6 sm:p-7 shadow-[0_25px_70px_rgba(0,0,0,0.5)] flex flex-col items-center text-center animate-ios-slide">
          
          <!-- Circular Radial Progress Indicator -->
          <div class="relative w-24 h-24 mb-5 flex items-center justify-center">
            <svg class="w-24 h-24 transform -rotate-90">
              <circle
                cx="48"
                cy="48"
                r="38"
                stroke="currentColor"
                stroke-width="7"
                fill="transparent"
                class="text-black/10 dark:text-white/10"
              />
              <circle
                cx="48"
                cy="48"
                r="38"
                stroke="currentColor"
                stroke-width="7"
                fill="transparent"
                stroke-linecap="round"
                [attr.stroke-dasharray]="238.76"
                [attr.stroke-dashoffset]="238.76 - (238.76 * uploadProgress()) / 100"
                class="transition-all duration-300"
                [class.text-ios-blue]="uploadProgress() < 100"
                [class.text-emerald-500]="uploadProgress() === 100"
              />
            </svg>

            <!-- Center Content: Percentage or Success Checkmark -->
            <div class="absolute inset-0 flex items-center justify-center">
              @if (uploadProgress() === 100) {
                <div class="w-12 h-12 rounded-full bg-emerald-500 text-white flex items-center justify-center shadow-lg shadow-emerald-500/30 scale-105 transition-transform animate-bounce">
                  <mat-icon class="!text-[28px]">check</mat-icon>
                </div>
              } @else {
                <span class="text-xl font-black font-mono tracking-tight text-slate-900 dark:text-white">
                  {{ uploadProgress() }}%
                </span>
              }
            </div>
          </div>

          <!-- Status Title & Phase Details -->
          @if (uploadProgress() === 100) {
            <h3 class="text-xl font-black text-emerald-600 dark:text-emerald-400 mb-1 animate-ios-fade">
              Added Successfully
            </h3>
            <p class="text-xs text-slate-500 dark:text-zinc-400 font-medium">
              Image cropped and stored successfully
            </p>
          } @else {
            <h3 class="text-lg font-black text-slate-900 dark:text-white mb-1">
              {{ progressMessage() }}
            </h3>
            <p class="text-xs text-slate-500 dark:text-zinc-400 font-medium">
              @switch (progressStep()) {
                @case ('processing') { Analyzing image frames and dimensions... }
                @case ('cropping') { Cropping image to selected frame size... }
                @case ('uploading') { Uploading cropped image to storage... }
              }
            </p>
          }

          <!-- Linear Progress Bar -->
          <div class="w-full bg-black/5 dark:bg-white/10 h-2 rounded-full overflow-hidden mt-5 mb-2">
            <div 
              class="h-full transition-all duration-300 rounded-full"
              [class.bg-ios-blue]="uploadProgress() < 100"
              [class.bg-emerald-500]="uploadProgress() === 100"
              [style.width.%]="uploadProgress()"
            ></div>
          </div>

          <!-- Step Indicators -->
          <div class="w-full flex justify-between text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-zinc-500 px-1 pt-1">
            <span [class.text-ios-blue]="uploadProgress() >= 20" [class.text-emerald-500]="uploadProgress() === 100">Processing</span>
            <span [class.text-ios-blue]="uploadProgress() >= 45" [class.text-emerald-500]="uploadProgress() === 100">Cropping</span>
            <span [class.text-ios-blue]="uploadProgress() >= 80" [class.text-emerald-500]="uploadProgress() === 100">Uploading</span>
            <span [class.text-emerald-500]="uploadProgress() === 100">Done</span>
          </div>

        </div>
      </div>
    }
  `,
})
export class AddImage {
  private authService = inject(AuthService);
  private notificationService = inject(NotificationService);

  @ViewChild('dropFrame') dropFrame?: ElementRef<HTMLDivElement>;
  @ViewChild('previewImg') previewImg?: ElementRef<HTMLImageElement>;

  isOpen = input<boolean>(false);
  closed = output<void>();
  imageUploaded = output<UserImage>();

  isPublic = signal<boolean>(true);
  selectedFile = signal<File | null>(null);
  selectedImagePreview = signal<string | null>(null);

  // Natural image dimensions
  naturalWidth = signal<number>(0);
  naturalHeight = signal<number>(0);

  // Computed displayed base dimensions
  displayedBaseWidth = signal<number>(360);
  displayedBaseHeight = signal<number>(360);

  // Manual pan, rotation and zoom controls
  panX = signal<number>(0);
  panY = signal<number>(0);
  rotationAngle = signal<number>(0);
  zoomScale = signal<number>(1.0);

  private isPanning = false;
  private startMouseX = 0;
  private startMouseY = 0;
  private initialPanX = 0;
  private initialPanY = 0;

  // Touch pinch-to-zoom tracking
  private touchStartDist = 0;
  private touchStartScale = 1.0;

  isActivelyAdjusting = signal<boolean>(false);
  isDraggingOver = signal<boolean>(false);
  isSubmitting = signal<boolean>(false);
  errorMessage = signal<string | null>(null);

  // Upload Progress Modal State
  showProgressModal = signal<boolean>(false);
  uploadProgress = signal<number>(0);
  progressStep = signal<'processing' | 'cropping' | 'uploading' | 'completed'>('processing');
  progressMessage = signal<string>('Processing image...');

  transformStyle = computed(() => {
    return `translate(${this.panX()}px, ${this.panY()}px) rotate(${this.rotationAngle()}deg) scale(${this.zoomScale()})`;
  });

  // Dynamic framing state calculation
  framingState = computed<FramingState>(() => {
    if (!this.selectedImagePreview()) return 'needs_adjustment';
    const scale = this.zoomScale();
    const px = Math.abs(this.panX());
    const py = Math.abs(this.panY());

    // Perfectly framed when scale fills frame well and pan is well aligned
    if (scale >= 0.98 && scale <= 1.6 && px < 45 && py < 45) {
      return 'perfectly_framed';
    } else if ((scale >= 0.82 && scale < 0.98) || (scale > 1.6 && scale <= 2.2) || (px < 90 && py < 90)) {
      return 'approaching_fit';
    } else {
      return 'needs_adjustment';
    }
  });

  toggleVisibility() {
    this.isPublic.update((v) => !v);
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.handleFile(input.files[0]);
    }
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingOver.set(true);
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingOver.set(false);
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingOver.set(false);
    if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
      this.handleFile(event.dataTransfer.files[0]);
    }
  }

  handleFile(file: File) {
    if (!file.type.startsWith('image/')) {
      this.errorMessage.set('Please select a valid image file.');
      return;
    }

    this.errorMessage.set(null);
    this.selectedFile.set(file);

    const reader = new FileReader();
    reader.onload = (e) => {
      this.selectedImagePreview.set(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  }

  onImageLoaded(event: Event) {
    const img = event.target as HTMLImageElement;
    if (img) {
      this.naturalWidth.set(img.naturalWidth || 600);
      this.naturalHeight.set(img.naturalHeight || 600);
      this.recalculateBaseDimensions();
      this.resetTransform();
    }
  }

  recalculateBaseDimensions() {
    const nw = this.naturalWidth();
    const nh = this.naturalHeight();
    if (!nw || !nh) return;

    const frameEl = this.dropFrame?.nativeElement;
    const frameSize = frameEl ? frameEl.clientWidth : 360;

    const imgAspect = nw / nh;
    let baseW = frameSize;
    let baseH = frameSize;

    if (imgAspect > 1) {
      baseW = frameSize * imgAspect;
      baseH = frameSize;
    } else {
      baseW = frameSize;
      baseH = frameSize / imgAspect;
    }

    this.displayedBaseWidth.set(baseW);
    this.displayedBaseHeight.set(baseH);
  }

  resetTransform() {
    this.panX.set(0);
    this.panY.set(0);
    this.rotationAngle.set(0);
    this.zoomScale.set(1.0);
  }

  rotateRight() {
    this.rotationAngle.update((a) => (a + 90) % 360);
  }

  stepZoom(delta: number) {
    const current = this.zoomScale();
    const next = Math.max(0.3, Math.min(3.0, current + delta));
    this.zoomScale.set(next);
  }

  onZoomChange(event: Event) {
    const input = event.target as HTMLInputElement;
    const val = parseFloat(input.value);
    if (!isNaN(val)) {
      this.zoomScale.set(val);
    }
  }

  onWheelZoom(event: WheelEvent) {
    if (!this.selectedImagePreview()) return;
    event.preventDefault();
    const delta = event.deltaY < 0 ? 0.05 : -0.05;
    this.stepZoom(delta);
  }

  // Pan controls
  startPan(event: MouseEvent) {
    if (!this.selectedImagePreview()) return;
    event.preventDefault();
    this.isPanning = true;
    this.isActivelyAdjusting.set(true);
    this.startMouseX = event.clientX;
    this.startMouseY = event.clientY;
    this.initialPanX = this.panX();
    this.initialPanY = this.panY();
  }

  onPan(event: MouseEvent) {
    if (!this.isPanning) return;
    event.preventDefault();
    const dx = event.clientX - this.startMouseX;
    const dy = event.clientY - this.startMouseY;
    this.panX.set(this.initialPanX + dx);
    this.panY.set(this.initialPanY + dy);
  }

  endPan() {
    this.isPanning = false;
    this.isActivelyAdjusting.set(false);
  }

  // Touch Pan & Pinch Controls
  startTouchPan(event: TouchEvent) {
    if (!this.selectedImagePreview()) return;
    if (event.touches.length === 1) {
      this.isPanning = true;
      this.isActivelyAdjusting.set(true);
      this.startMouseX = event.touches[0].clientX;
      this.startMouseY = event.touches[0].clientY;
      this.initialPanX = this.panX();
      this.initialPanY = this.panY();
    } else if (event.touches.length === 2) {
      this.isPanning = false;
      this.touchStartDist = this.getTouchDistance(event.touches);
      this.touchStartScale = this.zoomScale();
      this.isActivelyAdjusting.set(true);
    }
  }

  onTouchPan(event: TouchEvent) {
    if (!this.selectedImagePreview()) return;
    if (event.touches.length === 1 && this.isPanning) {
      const dx = event.touches[0].clientX - this.startMouseX;
      const dy = event.touches[0].clientY - this.startMouseY;
      this.panX.set(this.initialPanX + dx);
      this.panY.set(this.initialPanY + dy);
    } else if (event.touches.length === 2) {
      const dist = this.getTouchDistance(event.touches);
      if (this.touchStartDist > 0) {
        const factor = dist / this.touchStartDist;
        const newScale = Math.max(0.3, Math.min(3.0, this.touchStartScale * factor));
        this.zoomScale.set(newScale);
      }
    }
  }

  endTouchPan() {
    this.isPanning = false;
    this.isActivelyAdjusting.set(false);
    this.touchStartDist = 0;
  }

  private getTouchDistance(touches: TouchList): number {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  removeSelectedImage() {
    this.selectedFile.set(null);
    this.selectedImagePreview.set(null);
    this.resetTransform();
  }

  onBackdropClick(event: MouseEvent) {
    if (event.target === event.currentTarget) {
      this.close();
    }
  }

  close() {
    this.showProgressModal.set(false);
    this.closed.emit();
  }

  /**
   * Crops the selected image file directly to the 1:1 square frame visible in the preview,
   * applying the user's pan offsets, zoom scale, and rotation angle.
   */
  private cropImageToFrame(file: File): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);
      img.onload = () => {
        try {
          const frameEl = this.dropFrame?.nativeElement;
          const frameSize = frameEl ? frameEl.clientWidth : 360;

          // Target canvas resolution (1080x1080 high-res square)
          const targetSize = 1080;
          const canvas = document.createElement('canvas');
          canvas.width = targetSize;
          canvas.height = targetSize;
          const ctx = canvas.getContext('2d');

          if (!ctx) {
            URL.revokeObjectURL(objectUrl);
            reject(new Error('Canvas 2D context not available'));
            return;
          }

          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';

          const k = targetSize / frameSize;
          const baseW = this.displayedBaseWidth();
          const baseH = this.displayedBaseHeight();
          const panX = this.panX();
          const panY = this.panY();
          const angle = this.rotationAngle();
          const scale = this.zoomScale();

          ctx.translate(targetSize / 2 + panX * k, targetSize / 2 + panY * k);
          ctx.rotate((angle * Math.PI) / 180);
          ctx.scale(scale, scale);

          ctx.drawImage(img, -(baseW * k) / 2, -(baseH * k) / 2, baseW * k, baseH * k);

          canvas.toBlob(
            (blob) => {
              URL.revokeObjectURL(objectUrl);
              if (blob) {
                resolve(blob);
              } else {
                reject(new Error('Canvas crop export failed'));
              }
            },
            'image/jpeg',
            0.92
          );
        } catch (cropErr) {
          URL.revokeObjectURL(objectUrl);
          reject(cropErr);
        }
      };

      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('Failed to load image for cropping'));
      };

      img.src = objectUrl;
    });
  }

  async submitImage() {
    const file = this.selectedFile();
    if (!file) {
      this.errorMessage.set('Please select an image file first.');
      return;
    }

    if (this.framingState() !== 'perfectly_framed') {
      this.errorMessage.set('Please adjust zoom and position until the frame turns green (Perfectly Framed).');
      return;
    }

    const currentUser = this.authService.user();
    if (!currentUser) {
      this.errorMessage.set('You must be signed in to upload images.');
      return;
    }

    this.isSubmitting.set(true);
    this.errorMessage.set(null);

    // Phase 1: Processing
    this.showProgressModal.set(true);
    this.uploadProgress.set(15);
    this.progressStep.set('processing');
    this.progressMessage.set('Processing Image');

    try {
      await new Promise(r => setTimeout(r, 200));
      this.uploadProgress.set(30);

      // Phase 2: Cropping
      this.progressStep.set('cropping');
      this.progressMessage.set('Cropping to Selected Frame');
      const croppedBlob = await this.cropImageToFrame(file);
      this.uploadProgress.set(50);

      await new Promise(r => setTimeout(r, 150));

      // Phase 3: Uploading to Firebase Storage
      this.progressStep.set('uploading');
      this.progressMessage.set('Uploading Cropped Image');

      const timestamp = Date.now();
      const rawCleanName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_').replace(/\.[^/.]+$/, '');
      const cleanFileName = `${timestamp}_${rawCleanName}.jpg`;
      const storagePath = `accounts/${currentUser.uid}/images/${cleanFileName}`;
      const imageRef = ref(storage, storagePath);

      // Upload cropped blob with progress updates
      const uploadTask = uploadBytesResumable(imageRef, croppedBlob, {
        contentType: 'image/jpeg',
      });

      await new Promise<void>((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const percent = snapshot.totalBytes > 0
              ? Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 40)
              : 0;
            // Map progress smoothly between 50% and 90%
            this.uploadProgress.set(Math.min(90, 50 + percent));
          },
          (err) => reject(err),
          () => resolve()
        );
      });

      this.uploadProgress.set(92);
      const downloadURL = await getDownloadURL(imageRef);

      const imageId = `${timestamp}_${Math.random().toString(36).substring(2, 7)}`;
      const isPublicValue = this.isPublic();
      const now = new Date();
      const profile = this.authService.userProfile();
      const ownerName = profile?.displayName || currentUser.displayName || 'Anonymous Citizen';

      const imageData: UserImage = {
        id: imageId,
        ownerUid: currentUser.uid,
        authorUid: currentUser.uid,
        ownedBy: ownerName,
        uploadedBy: ownerName,
        imageUrl: downloadURL,
        visibility: isPublicValue ? 'public' : 'private',
        status: 'pending',
        remark: '',
        verificationStatus: 'pending',
        remarks: '',
        verifiedBy: '',
        viewedBy: [],
        likedBy: [],
        createdAt: now,
        updatedAt: now,
      };

      const docPayload = {
        ...imageData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      this.uploadProgress.set(96);
      const accountDocRef = doc(db, 'accounts', currentUser.uid, 'images', imageId);
      await setDoc(accountDocRef, docPayload);

      // Phase 4: 100% - Added Successfully
      this.uploadProgress.set(100);
      this.progressStep.set('completed');
      this.progressMessage.set('Added Successfully');

      // Wait briefly so the user sees 'Added Successfully' at 100%
      await new Promise(r => setTimeout(r, 1200));

      // Close all popups automatically
      this.showProgressModal.set(false);
      this.imageUploaded.emit(imageData);
      this.close();
      this.removeSelectedImage();
    } catch (err: unknown) {
      console.error('Error cropping/uploading image:', err);
      this.showProgressModal.set(false);
      try {
        handleFirestoreError(err, OperationType.WRITE, `accounts/${currentUser.uid}/images`);
      } catch {
        // Logged via handleFirestoreError
      }
      const msg = err instanceof Error ? err.message : 'Upload failed. Please try again.';
      this.errorMessage.set(msg);
    } finally {
      this.isSubmitting.set(false);
    }
  }
}
